// Textes du configurateur classique et de son panneau « lot −20 % », par langue (demande de Theo, 20/10/2026 :
// la pub Meta aux Pays-Bas envoie sur /nl/, où le configurateur restait en français ; TrueLang ne voit pas le widget).
//
// La langue est posée une fois au démarrage du widget (setConfiguratorLang, d'après <html lang> que pose TrueLang) ;
// sans appel (application Next, admin), tout reste en français.
// Les noms de produits et de catégories reprennent le dictionnaire du parcours lot (bundle-wizard/i18n-langs.js),
// complété ici par les textes propres au configurateur (étapes, question du charm, descriptions des corps de sac).
// Un texte absent du dictionnaire reste en français : jamais de trou à l'écran.
import WIZARD from '@/widget/bundle-wizard/i18n-langs.js'

type Lang = 'fr' | 'nl' | 'en' | 'es' | 'it' | 'de'
type Dict = Record<string, string>

const FR_UI = {
  loadError: 'Erreur de chargement du configurateur',
  buy: 'Acheter',
  adding: 'Ajout en cours…',
  added: 'Ajouté au panier !',
  cartError: "Erreur lors de l'ajout au panier",
  myWishlist: 'Ma wishlist',
  wishlist: 'Wishlist',
  saveSelection: 'Sauvegarder ma sélection',
  createWishlist: 'Créer ma wishlist',
  seeWishlist: 'Voir ma wishlist',
  closeWishlist: 'Fermer ma wishlist',
  remove: 'Supprimer',
  seeInfo: 'Voir les dimensions et infos',
  infoAria: 'Dimensions et infos',
  infoTitle: 'Dimensions & infos',
  dimensions: 'Dimensions',
  infos: 'Infos',
  close: 'Fermer',
  all: 'Tous',
  popular: 'Victime de son succès',
  outOfStock: 'En rupture',
  setOutOfStock: 'Un ou plusieurs produits de cet ensemble est en rupture.',
  wishlistFull: 'Wishlist pleine',
  wishlistFullText: 'Vous avez atteint la limite de {max} configurations. Supprimez-en quelques-unes pour en ajouter de nouvelles.',
  gotIt: 'Compris',
  emailInvalid: 'Veuillez entrer une adresse email valide',
  emailIntro: 'Entrez votre email pour sauvegarder vos configurations.',
  emailHave: 'Vous avez déjà une wishlist ?',
  emailHaveText: 'Retrouvez-la automatiquement sur tous vos appareils en utilisant votre email.',
  emailPlaceholder: 'votre@email.com',
  continue: 'Continuer',
  // Lot −20 % depuis le configurateur (tutoiement, comme le reste du parcours)
  luPct: '−{d} %',
  luEyebrow: '−{d} % sur tout',
  luTitle: 'Ajoute 2 rabats et passe ton sac en lot',
  luText: 'Tu gardes tout ce que tu as choisi. Ajoute 2 rabats : tout ton sac passe à −{d} %.',
  luYourFlap: 'Ton rabat',
  luChoose2: 'Choisir mon 2e rabat',
  luChoose3: 'Choisir mon 3e rabat',
  luFlap1: '1er rabat',
  luFlap2: '2e rabat',
  luFlap3: '3e rabat',
  luBag1: 'Ton sac (1 rabat)',
  luBag3: 'Ton sac + 2 rabats',
  luNote: 'Soit 2 rabats de plus pour {price}, et la livraison en point relais offerte.',
  luCta: 'Choisir mes 2 rabats →',
  luHint: 'Passer ce sac en lot avec 2 rabats de plus',
  luWithClip: ' avec ton fermoir {name}',
  luTouch: "Touche un rabat pour l'essayer sur ton sac",
  luStep: 'Étape {n} sur 3',
  luRecap: 'Récapitulatif',
  luTag1: 'Ton 1er rabat',
  luTaken: 'Déjà choisi',
  luLot: 'Ton lot −{d} %',
  luKept: 'Ton sac est gardé tel quel',
  luWhich2: 'Quel 2<sup>e</sup> rabat ?',
  luWhich3: 'Quel 3<sup>e</sup> rabat ?',
  luAdds: "Il s'ajoute à ton sac, à −{d} %",
  luMore: "Envie d'autres accessoires à −{d} % ?",
  luOptional: 'Facultatif : coche ce qui te plaît, plusieurs choix possibles',
  luYourBag: 'Ton sac',
  luRemove: 'Retirer',
  luWithout: 'Sans le lot : ',
  luLotPrice: 'Ton lot : {price}',
  luSave: 'Tu économises {amount} · livraison en point relais offerte',
  luDone: 'Ton lot est dans ton panier',
  luDoneText: 'Tu économises {amount}, et la livraison en point relais est offerte.',
  luNextFlap: 'Rabat suivant →',
  luBack: '← Retour',
  luNext: 'Suivant →',
  luRecapN: 'Récapitulatif ({n}) →',
  luNoThanks: 'Non merci, récapitulatif →',
  luAdd: 'Ajouter mon lot · {price}',
  luCartError: "Le lot n'a pas pu être ajouté au panier",
  // Mode swipe ([saas_swiper], page « swipe-ton-sac »)
  swLoading: 'Chargement...',
  swNotFound: 'Configurateur introuvable',
  swDone: "C'est fini !",
  swDoneText: 'Vous avez vu toutes les combinaisons.',
  swSaved1: '{n} like sauvegardé.',
  swSavedN: '{n} likes sauvegardés.',
  swRestart: 'Recommencer',
  swPass: 'Passer',
  swSuper: 'Super like (wishlist)',
  swLike: "J'aime",
  swMyLikes: 'Mes likes ({n})',
  swEmpty: 'Swipez vers la droite pour liker',
}
type UiKey = keyof typeof FR_UI

const UI: Record<Exclude<Lang, 'fr'>, Partial<Record<UiKey, string>>> = {
  // Néerlandais : vocabulaire du parcours lot (pakket, klep, riem, winkelmandje, afhaalpunt), tutoiement (je)
  nl: {
    loadError: 'De configurator kan niet worden geladen',
    buy: 'Kopen',
    adding: 'Bezig met toevoegen…',
    added: 'Toegevoegd aan je winkelmandje!',
    cartError: 'Het product kon niet aan je winkelmandje worden toegevoegd',
    myWishlist: 'Mijn wishlist',
    saveSelection: 'Mijn selectie bewaren',
    createWishlist: 'Mijn wishlist aanmaken',
    seeWishlist: 'Mijn wishlist bekijken',
    closeWishlist: 'Mijn wishlist sluiten',
    remove: 'Verwijderen',
    seeInfo: 'Afmetingen en info bekijken',
    infoAria: 'Afmetingen en info',
    infoTitle: 'Afmetingen & info',
    dimensions: 'Afmetingen',
    infos: 'Info',
    close: 'Sluiten',
    all: 'Alle',
    popular: 'Zo populair',
    outOfStock: 'Uitverkocht',
    setOutOfStock: 'Een of meer producten van dit ontwerp zijn uitverkocht.',
    wishlistFull: 'Wishlist vol',
    wishlistFullText: 'Je hebt het maximum van {max} ontwerpen bereikt. Verwijder er een paar om nieuwe toe te voegen.',
    gotIt: 'Begrepen',
    emailInvalid: 'Vul een geldig e-mailadres in',
    emailIntro: 'Vul je e-mailadres in om je ontwerpen te bewaren.',
    emailHave: 'Heb je al een wishlist?',
    emailHaveText: 'Met je e-mailadres vind je hem automatisch terug op al je apparaten.',
    emailPlaceholder: 'jij@email.nl',
    continue: 'Doorgaan',
    luPct: '−{d}%',
    luEyebrow: '−{d}% op alles',
    luTitle: 'Voeg 2 kleppen toe en maak van je tas een pakket',
    luText: 'Je houdt alles wat je hebt gekozen. Voeg 2 kleppen toe: je hele tas krijgt −{d}% korting.',
    luYourFlap: 'Jouw klep',
    luChoose2: 'Mijn 2e klep kiezen',
    luChoose3: 'Mijn 3e klep kiezen',
    luFlap1: '1e klep',
    luFlap2: '2e klep',
    luFlap3: '3e klep',
    luBag1: 'Jouw tas (1 klep)',
    luBag3: 'Jouw tas + 2 kleppen',
    luNote: 'Dus 2 kleppen extra voor {price}, en gratis verzending naar een afhaalpunt.',
    luCta: 'Kies mijn 2 kleppen →',
    luHint: 'Maak van deze tas een pakket met 2 kleppen extra',
    luWithClip: ' met je sluiting {name}',
    luTouch: 'Tik op een klep om hem op je tas te passen',
    luStep: 'Stap {n} van 3',
    luRecap: 'Overzicht',
    luTag1: 'Jouw 1e klep',
    luTaken: 'Al gekozen',
    luLot: 'Jouw pakket −{d}%',
    luKept: 'Je tas blijft precies zoals hij is',
    luWhich2: 'Welke 2<sup>e</sup> klep?',
    luWhich3: 'Welke 3<sup>e</sup> klep?',
    luAdds: 'Hij komt bij je tas, met −{d}% korting',
    luMore: 'Zin in meer accessoires met −{d}% korting?',
    luOptional: 'Niet verplicht: vink aan wat je mooi vindt, je kunt er meerdere kiezen',
    luYourBag: 'Jouw tas',
    luRemove: 'Verwijderen',
    luWithout: 'Zonder pakket: ',
    luLotPrice: 'Jouw pakket: {price}',
    luSave: 'Je bespaart {amount} · gratis verzending naar een afhaalpunt',
    luDone: 'Je pakket zit in je winkelmandje',
    luDoneText: 'Je bespaart {amount} en de verzending naar een afhaalpunt is gratis.',
    luNextFlap: 'Volgende klep →',
    luBack: '← Terug',
    luNext: 'Volgende →',
    luRecapN: 'Overzicht ({n}) →',
    luNoThanks: 'Nee bedankt, naar het overzicht →',
    luAdd: 'Pakket toevoegen · {price}',
    luCartError: 'Het pakket kon niet aan je winkelmandje worden toegevoegd',
    swLoading: 'Laden…', swNotFound: 'Configurator niet gevonden', swDone: 'Klaar!', swDoneText: 'Je hebt alle combinaties gezien.',
    swSaved1: '{n} like bewaard.', swSavedN: '{n} likes bewaard.', swRestart: 'Opnieuw beginnen', swPass: 'Overslaan',
    swSuper: 'Superlike (wishlist)', swLike: 'Vind ik leuk', swMyLikes: 'Mijn likes ({n})', swEmpty: 'Swipe naar rechts om te liken',
  },
  en: {
    loadError: "The configurator couldn't be loaded",
    buy: 'Buy',
    adding: 'Adding…',
    added: 'Added to cart!',
    cartError: "Couldn't add to cart",
    myWishlist: 'My wishlist',
    saveSelection: 'Save my selection',
    createWishlist: 'Create my wishlist',
    seeWishlist: 'See my wishlist',
    closeWishlist: 'Close my wishlist',
    remove: 'Delete',
    seeInfo: 'See dimensions and details',
    infoAria: 'Dimensions and details',
    infoTitle: 'Dimensions & details',
    dimensions: 'Dimensions',
    infos: 'Details',
    close: 'Close',
    all: 'All',
    popular: 'So popular',
    outOfStock: 'Out of stock',
    setOutOfStock: 'One or more items in this design are out of stock.',
    wishlistFull: 'Wishlist full',
    wishlistFullText: 'You have reached the limit of {max} designs. Delete a few to add new ones.',
    gotIt: 'Got it',
    emailInvalid: 'Please enter a valid email address',
    emailIntro: 'Enter your email to save your designs.',
    emailHave: 'Already have a wishlist?',
    emailHaveText: 'Use your email to find it automatically on all your devices.',
    emailPlaceholder: 'you@email.com',
    continue: 'Continue',
    luPct: '−{d}%',
    luEyebrow: '{d}% off everything',
    luTitle: 'Add 2 flaps and turn your bag into a bundle',
    luText: 'You keep everything you chose. Add 2 flaps and your whole bag gets {d}% off.',
    luYourFlap: 'Your flap',
    luChoose2: 'Choose my 2nd flap',
    luChoose3: 'Choose my 3rd flap',
    luFlap1: '1st flap',
    luFlap2: '2nd flap',
    luFlap3: '3rd flap',
    luBag1: 'Your bag (1 flap)',
    luBag3: 'Your bag + 2 flaps',
    luNote: 'That is 2 more flaps for {price}, plus free shipping to a pickup point.',
    luCta: 'Choose my 2 flaps →',
    luHint: 'Turn this bag into a bundle with 2 more flaps',
    luWithClip: ' with your {name} clasp',
    luTouch: 'Tap a flap to try it on your bag',
    luStep: 'Step {n} of 3',
    luRecap: 'Summary',
    luTag1: 'Your 1st flap',
    luTaken: 'Already chosen',
    luLot: 'Your bundle: {d}% off',
    luKept: 'Your bag stays exactly as it is',
    luWhich2: 'Which 2<sup>nd</sup> flap?',
    luWhich3: 'Which 3<sup>rd</sup> flap?',
    luAdds: 'It is added to your bag at {d}% off',
    luMore: 'Fancy more accessories at {d}% off?',
    luOptional: 'Optional: tick what you like, you can pick several',
    luYourBag: 'Your bag',
    luRemove: 'Remove',
    luWithout: 'Without the bundle: ',
    luLotPrice: 'Your bundle: {price}',
    luSave: 'You save {amount} · free shipping to a pickup point',
    luDone: 'Your bundle is in your cart',
    luDoneText: 'You save {amount}, and shipping to a pickup point is free.',
    luNextFlap: 'Next flap →',
    luBack: '← Back',
    luNext: 'Next →',
    luRecapN: 'Summary ({n}) →',
    luNoThanks: 'No thanks, see summary →',
    luAdd: 'Add my bundle · {price}',
    luCartError: "The bundle couldn't be added to your cart",
    swLoading: 'Loading…', swNotFound: 'Configurator not found', swDone: "That's all!", swDoneText: "You've seen every combination.",
    swSaved1: '{n} like saved.', swSavedN: '{n} likes saved.', swRestart: 'Start over', swPass: 'Skip',
    swSuper: 'Super like (wishlist)', swLike: 'Like', swMyLikes: 'My likes ({n})', swEmpty: 'Swipe right to like',
  },
  es: {
    loadError: 'No se ha podido cargar el configurador',
    buy: 'Comprar',
    adding: 'Añadiendo…',
    added: '¡Añadido al carrito!',
    cartError: 'No se ha podido añadir al carrito',
    myWishlist: 'Mi lista de deseos',
    wishlist: 'Lista de deseos',
    saveSelection: 'Guardar mi selección',
    createWishlist: 'Crear mi lista de deseos',
    seeWishlist: 'Ver mi lista de deseos',
    closeWishlist: 'Cerrar mi lista de deseos',
    remove: 'Eliminar',
    seeInfo: 'Ver medidas e información',
    infoAria: 'Medidas e información',
    infoTitle: 'Medidas e información',
    dimensions: 'Medidas',
    infos: 'Información',
    close: 'Cerrar',
    all: 'Todos',
    popular: 'Todo un éxito',
    outOfStock: 'Agotado',
    setOutOfStock: 'Uno o varios productos de este diseño están agotados.',
    wishlistFull: 'Lista de deseos llena',
    wishlistFullText: 'Has alcanzado el límite de {max} diseños. Elimina algunos para añadir otros nuevos.',
    gotIt: 'Entendido',
    emailInvalid: 'Introduce un email válido',
    emailIntro: 'Introduce tu email para guardar tus diseños.',
    emailHave: '¿Ya tienes una lista de deseos?',
    emailHaveText: 'Con tu email la recuperas automáticamente en todos tus dispositivos.',
    emailPlaceholder: 'tu@email.com',
    continue: 'Continuar',
    luEyebrow: '−{d} % en todo',
    luTitle: 'Añade 2 solapas y convierte tu bolso en un lote',
    luText: 'Conservas todo lo que has elegido. Añade 2 solapas y todo tu bolso tiene un −{d} %.',
    luYourFlap: 'Tu solapa',
    luChoose2: 'Elegir mi 2.ª solapa',
    luChoose3: 'Elegir mi 3.ª solapa',
    luFlap1: '1.ª solapa',
    luFlap2: '2.ª solapa',
    luFlap3: '3.ª solapa',
    luBag1: 'Tu bolso (1 solapa)',
    luBag3: 'Tu bolso + 2 solapas',
    luNote: 'Es decir, 2 solapas más por {price}, y envío gratis a punto de recogida.',
    luCta: 'Elegir mis 2 solapas →',
    luHint: 'Convierte este bolso en un lote con 2 solapas más',
    luWithClip: ' con tu cierre {name}',
    luTouch: 'Toca una solapa para probarla en tu bolso',
    luStep: 'Paso {n} de 3',
    luRecap: 'Resumen',
    luTag1: 'Tu 1.ª solapa',
    luTaken: 'Ya elegida',
    luLot: 'Tu lote −{d} %',
    luKept: 'Tu bolso se queda tal cual',
    luWhich2: '¿Qué 2.<sup>ª</sup> solapa?',
    luWhich3: '¿Qué 3.<sup>ª</sup> solapa?',
    luAdds: 'Se añade a tu bolso con un −{d} %',
    luMore: '¿Te apetecen más accesorios con un −{d} %?',
    luOptional: 'Opcional: marca lo que te guste, puedes elegir varios',
    luYourBag: 'Tu bolso',
    luRemove: 'Quitar',
    luWithout: 'Sin el lote: ',
    luLotPrice: 'Tu lote: {price}',
    luSave: 'Ahorras {amount} · envío gratis a punto de recogida',
    luDone: 'Tu lote está en tu carrito',
    luDoneText: 'Ahorras {amount}, y el envío a punto de recogida es gratis.',
    luNextFlap: 'Siguiente solapa →',
    luBack: '← Volver',
    luNext: 'Siguiente →',
    luRecapN: 'Resumen ({n}) →',
    luNoThanks: 'No, gracias, ver resumen →',
    luAdd: 'Añadir mi lote · {price}',
    luCartError: 'No se ha podido añadir el lote al carrito',
    swLoading: 'Cargando…', swNotFound: 'Configurador no encontrado', swDone: '¡Se acabó!', swDoneText: 'Has visto todas las combinaciones.',
    swSaved1: '{n} like guardado.', swSavedN: '{n} likes guardados.', swRestart: 'Empezar de nuevo', swPass: 'Pasar',
    swSuper: 'Super like (lista de deseos)', swLike: 'Me gusta', swMyLikes: 'Mis likes ({n})', swEmpty: 'Desliza a la derecha para dar like',
  },
  it: {
    loadError: 'Impossibile caricare il configuratore',
    buy: 'Acquista',
    adding: 'Aggiunta in corso…',
    added: 'Aggiunto al carrello!',
    cartError: 'Impossibile aggiungere al carrello',
    myWishlist: 'La mia wishlist',
    saveSelection: 'Salva la mia selezione',
    createWishlist: 'Crea la mia wishlist',
    seeWishlist: 'Vedi la mia wishlist',
    closeWishlist: 'Chiudi la mia wishlist',
    remove: 'Elimina',
    seeInfo: 'Vedi dimensioni e info',
    infoAria: 'Dimensioni e info',
    infoTitle: 'Dimensioni e info',
    dimensions: 'Dimensioni',
    infos: 'Info',
    close: 'Chiudi',
    all: 'Tutti',
    popular: 'Un vero successo',
    outOfStock: 'Esaurito',
    setOutOfStock: 'Uno o più prodotti di questa creazione sono esauriti.',
    wishlistFull: 'Wishlist piena',
    wishlistFullText: 'Hai raggiunto il limite di {max} creazioni. Eliminane qualcuna per aggiungerne di nuove.',
    gotIt: 'Ho capito',
    emailInvalid: 'Inserisci un indirizzo email valido',
    emailIntro: 'Inserisci la tua email per salvare le tue creazioni.',
    emailHave: 'Hai già una wishlist?',
    emailHaveText: 'Con la tua email la ritrovi automaticamente su tutti i tuoi dispositivi.',
    emailPlaceholder: 'tu@email.com',
    continue: 'Continua',
    luPct: '−{d}%',
    luEyebrow: '−{d}% su tutto',
    luTitle: 'Aggiungi 2 patte e trasforma la tua borsa in un set',
    luText: 'Tieni tutto quello che hai scelto. Aggiungi 2 patte: tutta la tua borsa passa a −{d}%.',
    luYourFlap: 'La tua patta',
    luChoose2: 'Scegli la mia 2ª patta',
    luChoose3: 'Scegli la mia 3ª patta',
    luFlap1: '1ª patta',
    luFlap2: '2ª patta',
    luFlap3: '3ª patta',
    luBag1: 'La tua borsa (1 patta)',
    luBag3: 'La tua borsa + 2 patte',
    luNote: 'Cioè 2 patte in più per {price}, e spedizione gratuita al punto di ritiro.',
    luCta: 'Scegli le mie 2 patte →',
    luHint: 'Trasforma questa borsa in un set con 2 patte in più',
    luWithClip: ' con la tua chiusura {name}',
    luTouch: 'Tocca una patta per provarla sulla tua borsa',
    luStep: 'Passo {n} di 3',
    luRecap: 'Riepilogo',
    luTag1: 'La tua 1ª patta',
    luTaken: 'Già scelta',
    luLot: 'Il tuo set −{d}%',
    luKept: 'La tua borsa resta così com’è',
    luWhich2: 'Quale 2<sup>a</sup> patta?',
    luWhich3: 'Quale 3<sup>a</sup> patta?',
    luAdds: 'Si aggiunge alla tua borsa, a −{d}%',
    luMore: 'Ti vanno altri accessori a −{d}%?',
    luOptional: 'Facoltativo: spunta quello che ti piace, puoi sceglierne più di uno',
    luYourBag: 'La tua borsa',
    luRemove: 'Rimuovi',
    luWithout: 'Senza il set: ',
    luLotPrice: 'Il tuo set: {price}',
    luSave: 'Risparmi {amount} · spedizione gratuita al punto di ritiro',
    luDone: 'Il tuo set è nel carrello',
    luDoneText: 'Risparmi {amount}, e la spedizione al punto di ritiro è gratuita.',
    luNextFlap: 'Patta successiva →',
    luBack: '← Indietro',
    luNext: 'Avanti →',
    luRecapN: 'Riepilogo ({n}) →',
    luNoThanks: 'No grazie, vai al riepilogo →',
    luAdd: 'Aggiungi il mio set · {price}',
    luCartError: 'Impossibile aggiungere il set al carrello',
    swLoading: 'Caricamento…', swNotFound: 'Configuratore non trovato', swDone: 'Finito!', swDoneText: 'Hai visto tutte le combinazioni.',
    swSaved1: '{n} like salvato.', swSavedN: '{n} like salvati.', swRestart: 'Ricomincia', swPass: 'Salta',
    swSuper: 'Super like (wishlist)', swLike: 'Mi piace', swMyLikes: 'I miei like ({n})', swEmpty: 'Scorri a destra per mettere like',
  },
  // Allemand : vocabulaire du parcours lot (Set, Klappe, Riemen, Verschluss, Anhänger, Paketshop), tutoiement (du)
  de: {
    loadError: 'Der Konfigurator konnte nicht geladen werden',
    buy: 'Kaufen',
    adding: 'Wird hinzugefügt…',
    added: 'In den Warenkorb gelegt!',
    cartError: 'Das Produkt konnte nicht in den Warenkorb gelegt werden',
    myWishlist: 'Meine Wunschliste',
    wishlist: 'Wunschliste',
    saveSelection: 'Meine Auswahl speichern',
    createWishlist: 'Meine Wunschliste anlegen',
    seeWishlist: 'Meine Wunschliste ansehen',
    closeWishlist: 'Meine Wunschliste schließen',
    remove: 'Löschen',
    seeInfo: 'Maße und Infos ansehen',
    infoAria: 'Maße und Infos',
    infoTitle: 'Maße & Infos',
    dimensions: 'Maße',
    infos: 'Infos',
    close: 'Schließen',
    all: 'Alle',
    popular: 'Sehr gefragt',
    outOfStock: 'Ausverkauft',
    setOutOfStock: 'Ein oder mehrere Produkte dieser Kreation sind ausverkauft.',
    wishlistFull: 'Wunschliste voll',
    wishlistFullText: 'Du hast das Maximum von {max} Kreationen erreicht. Lösche ein paar, um neue hinzuzufügen.',
    gotIt: 'Verstanden',
    emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein',
    emailIntro: 'Gib deine E-Mail-Adresse ein, um deine Kreationen zu speichern.',
    emailHave: 'Du hast schon eine Wunschliste?',
    emailHaveText: 'Mit deiner E-Mail-Adresse findest du sie automatisch auf all deinen Geräten wieder.',
    emailPlaceholder: 'du@email.de',
    continue: 'Weiter',
    luEyebrow: '−{d} % auf alles',
    luTitle: 'Füge 2 Klappen hinzu und mach aus deiner Tasche ein Set',
    luText: 'Du behältst alles, was du gewählt hast. Füge 2 Klappen hinzu: Deine ganze Tasche bekommt −{d} %.',
    luYourFlap: 'Deine Klappe',
    luChoose2: 'Meine 2. Klappe wählen',
    luChoose3: 'Meine 3. Klappe wählen',
    luFlap1: '1. Klappe',
    luFlap2: '2. Klappe',
    luFlap3: '3. Klappe',
    luBag1: 'Deine Tasche (1 Klappe)',
    luBag3: 'Deine Tasche + 2 Klappen',
    luNote: 'Also 2 Klappen mehr für {price}, dazu kostenloser Versand an einen Paketshop.',
    luCta: 'Meine 2 Klappen wählen →',
    luHint: 'Mach aus dieser Tasche ein Set mit 2 Klappen mehr',
    luWithClip: ' mit deinem Verschluss {name}',
    luTouch: 'Tippe auf eine Klappe, um sie an deiner Tasche auszuprobieren',
    luStep: 'Schritt {n} von 3',
    luRecap: 'Übersicht',
    luTag1: 'Deine 1. Klappe',
    luTaken: 'Schon gewählt',
    luLot: 'Dein Set −{d} %',
    luKept: 'Deine Tasche bleibt genau so, wie sie ist',
    luWhich2: 'Welche 2. Klappe?',
    luWhich3: 'Welche 3. Klappe?',
    luAdds: 'Sie kommt zu deiner Tasche dazu, mit −{d} %',
    luMore: 'Lust auf weitere Accessoires mit −{d} %?',
    luOptional: 'Freiwillig: Hake an, was dir gefällt, du kannst mehrere wählen',
    luYourBag: 'Deine Tasche',
    luRemove: 'Entfernen',
    luWithout: 'Ohne Set: ',
    luLotPrice: 'Dein Set: {price}',
    luSave: 'Du sparst {amount} · kostenloser Versand an einen Paketshop',
    luDone: 'Dein Set liegt im Warenkorb',
    luDoneText: 'Du sparst {amount}, und der Versand an einen Paketshop ist kostenlos.',
    luNextFlap: 'Nächste Klappe →',
    luBack: '← Zurück',
    luNext: 'Weiter →',
    luRecapN: 'Übersicht ({n}) →',
    luNoThanks: 'Nein danke, zur Übersicht →',
    luAdd: 'Mein Set hinzufügen · {price}',
    luCartError: 'Das Set konnte nicht in den Warenkorb gelegt werden',
    swLoading: 'Wird geladen…', swNotFound: 'Konfigurator nicht gefunden', swDone: 'Fertig!', swDoneText: 'Du hast alle Kombinationen gesehen.',
    swSaved1: '{n} Like gespeichert.', swSavedN: '{n} Likes gespeichert.', swRestart: 'Nochmal von vorn', swPass: 'Überspringen',
    swSuper: 'Super-Like (Wunschliste)', swLike: 'Gefällt mir', swMyLikes: 'Meine Likes ({n})', swEmpty: 'Wische nach rechts, um zu liken',
  },
}

// Textes saisis dans le configurateur (SaaS), propres au configurateur classique ; le reste vient du parcours lot.
// Les descriptions des corps de sac sont traduites ligne par ligne : une ligne modifiée dans le SaaS reste en français
// jusqu'à ce qu'on l'ajoute ici, sans casser les autres.
const TERMS: Record<Exclude<Lang, 'fr'>, Dict> = {
  nl: {
    'Fermoirs non perforé': 'Sluitingen', 'Fermoirs perforés': 'Geperforeerde sluitingen', 'Choisis ton confort': 'Kies hoe je hem draagt',
    'Un charm ?': 'Een bedel?', 'Sans charm': 'Zonder bedel', 'Avec un charm +3€': 'Met een bedel +3 €',
    'Sangle épaule avec anneaux dorés': 'Schouderriem met gouden ringen',
    'Corps de sac noir format "essentiel". Un corps de sac au format compact, pensé pour emmener uniquement vos essentiels avec vous. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Zwart taslichaam in het formaat "Het essentiële". Een compact taslichaam, bedacht om alleen je essentials mee te nemen. Je draagt hem schuin over je lichaam of over je schouder, afhankelijk van de gekozen riem, voor elk moment van je dag.',
    'Corps de sac noir format "équilibre parfait". Un corps de sac au format confort, pensé pour emmener tout ce dont vous avez besoin. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Zwart taslichaam in het formaat "Perfecte balans". Een comfortabel formaat, bedacht om alles mee te nemen wat je nodig hebt. Je draagt hem schuin over je lichaam of over je schouder, afhankelijk van de gekozen riem, voor elk moment van je dag.',
    'Corps de sac noir format "Intégral". Un corps de sac grand format, pensé pour tout ce dont vous avez besoin, sans vous priver de rien. Il se porte à l\'épaule grâce à ses deux hanses incluses.':
      'Zwart taslichaam in het formaat "Alles erin". Een groot taslichaam, bedacht voor alles wat je nodig hebt, zonder iets thuis te laten. Je draagt hem over je schouder met de twee meegeleverde hengsels.',
    'Zipper intérieur et fermeture éclair en forme de coeur.': 'Binnenrits en een ritssluiting met een hartje.',
    'Zipper intérieur et fermeture éclair dorée en forme de coeur.': 'Binnenrits en een goudkleurige ritssluiting met een hartje.',
    '2 poches intérieures dont une à zip.': '2 binnenvakken, waarvan één met rits.',
    '1 grande poche intérieure + 2 petites.': '1 groot binnenvak + 2 kleine.',
    '1 grande poche intérieure avec fermeture à pression + 2 petites.': '1 groot binnenvak met drukknoop + 2 kleine.',
    'Doublure intérieure en simili vegan rouge.': 'Rode voering van vegan kunstleer.',
    'Doublure microfibre intérieure rouge.': 'Rode voering van microvezel.',
    'Un séparateur central vous offrant 2 compartiments de rangement.': 'Een tussenschot in het midden, dus 2 opbergvakken.',
    'Grande surface de rangement, idéal pour transporter un ordinateur portable.': 'Veel opbergruimte, ideaal om een laptop mee te nemen.',
    "Un fermoir aimanté à l'avant pour la fermeture du sac.": 'Een magneetsluiting aan de voorkant om de tas te sluiten.',
    "Fermeture aimantée à l'avant pour la fermeture du sac.": 'Magneetsluiting aan de voorkant om de tas te sluiten.',
    "3 boutons à pression à l'arrière du sac pour clipper le rabat.": '3 drukknopen aan de achterkant om de klep vast te klikken.',
    "3 boutons à pression forte tenue à l'arrière du sac pour clipper le rabat.": '3 extra stevige drukknopen aan de achterkant om de klep vast te klikken.',
    '4 pieds de protection en métal sous le sac': '4 metalen beschermvoetjes onder de tas',
    '2 hanses noires épaule avec anneaux métal amovibles vendues avec le sac.': '2 zwarte schouderhengsels met afneembare metalen ringen, meegeleverd met de tas.',
    'À personnaliser avec les accessoires de votre choix pour créer le sac idéal. Logo "And Joy" embossé en doré à l\'avant du sac.':
      'Maak hem helemaal van jou met de accessoires die je kiest. Goudkleurig "And Joy"-logo in reliëf op de voorkant.',
    'Matière premium synthétique recyclée labellisée Global Recycled Standards.': 'Premium gerecycled synthetisch materiaal met het Global Recycled Standard-label.',
    'Dessiné en France à Lyon et façonné à la main en Italie, dans un petit atelier Florentin.': 'Ontworpen in Lyon (Frankrijk) en met de hand gemaakt in een klein atelier in Florence (Italië).',
  },
  en: {
    'Fermoirs non perforé': 'Clasps', 'Fermoirs perforés': 'Perforated clasps', 'Choisis ton confort': 'Choose how to carry it',
    'Un charm ?': 'A charm?', 'Sans charm': 'No charm', 'Avec un charm +3€': 'With a charm +€3',
    'Sangle épaule avec anneaux dorés': 'Shoulder strap with gold rings',
    'Corps de sac noir format "essentiel". Un corps de sac au format compact, pensé pour emmener uniquement vos essentiels avec vous. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Black bag body in the "Essential" size. A compact bag body, designed to carry just your essentials. Wear it crossbody or on the shoulder, depending on the strap you choose, for every moment of your day.',
    'Corps de sac noir format "équilibre parfait". Un corps de sac au format confort, pensé pour emmener tout ce dont vous avez besoin. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Black bag body in the "Perfect Balance" size. A comfortable size, designed to carry everything you need. Wear it crossbody or on the shoulder, depending on the strap you choose, for every moment of your day.',
    'Corps de sac noir format "Intégral". Un corps de sac grand format, pensé pour tout ce dont vous avez besoin, sans vous priver de rien. Il se porte à l\'épaule grâce à ses deux hanses incluses.':
      'Black bag body in the "Complete" size. A large bag body, designed for everything you need, without leaving anything behind. Carry it on the shoulder with its two included handles.',
    'Zipper intérieur et fermeture éclair en forme de coeur.': 'Inner zip and a heart-shaped zip pull.',
    'Zipper intérieur et fermeture éclair dorée en forme de coeur.': 'Inner zip and a gold heart-shaped zip pull.',
    '2 poches intérieures dont une à zip.': '2 inner pockets, one with a zip.',
    '1 grande poche intérieure + 2 petites.': '1 large inner pocket + 2 small ones.',
    '1 grande poche intérieure avec fermeture à pression + 2 petites.': '1 large inner pocket with a snap closure + 2 small ones.',
    'Doublure intérieure en simili vegan rouge.': 'Red vegan faux leather lining.',
    'Doublure microfibre intérieure rouge.': 'Red microfiber lining.',
    'Un séparateur central vous offrant 2 compartiments de rangement.': 'A central divider giving you 2 compartments.',
    'Grande surface de rangement, idéal pour transporter un ordinateur portable.': 'Lots of storage space, ideal for carrying a laptop.',
    "Un fermoir aimanté à l'avant pour la fermeture du sac.": 'A magnetic clasp on the front to close the bag.',
    "Fermeture aimantée à l'avant pour la fermeture du sac.": 'Magnetic closure on the front to close the bag.',
    "3 boutons à pression à l'arrière du sac pour clipper le rabat.": '3 snap buttons on the back to clip on the flap.',
    "3 boutons à pression forte tenue à l'arrière du sac pour clipper le rabat.": '3 extra-strong snap buttons on the back to clip on the flap.',
    '4 pieds de protection en métal sous le sac': '4 protective metal feet under the bag',
    '2 hanses noires épaule avec anneaux métal amovibles vendues avec le sac.': '2 black shoulder handles with removable metal rings, sold with the bag.',
    'À personnaliser avec les accessoires de votre choix pour créer le sac idéal. Logo "And Joy" embossé en doré à l\'avant du sac.':
      'Customize it with the accessories of your choice to create your ideal bag. Gold embossed "And Joy" logo on the front.',
    'Matière premium synthétique recyclée labellisée Global Recycled Standards.': 'Premium recycled synthetic material, Global Recycled Standard certified.',
    'Dessiné en France à Lyon et façonné à la main en Italie, dans un petit atelier Florentin.': 'Designed in Lyon, France, and handmade in Italy in a small Florentine workshop.',
  },
  es: {
    'Fermoirs non perforé': 'Cierres', 'Fermoirs perforés': 'Cierres perforados', 'Choisis ton confort': 'Elige cómo llevarlo',
    'Un charm ?': '¿Un charm?', 'Sans charm': 'Sin charm', 'Avec un charm +3€': 'Con un charm +3 €',
    'Sangle épaule avec anneaux dorés': 'Correa de hombro con anillas doradas',
    'Corps de sac noir format "essentiel". Un corps de sac au format compact, pensé pour emmener uniquement vos essentiels avec vous. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Cuerpo de bolso negro en formato "Lo esencial". Un cuerpo de bolso compacto, pensado para llevar solo lo imprescindible. Se lleva cruzado o al hombro, según la correa elegida, para acompañarte en tu día a día.',
    'Corps de sac noir format "équilibre parfait". Un corps de sac au format confort, pensé pour emmener tout ce dont vous avez besoin. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Cuerpo de bolso negro en formato "Equilibrio perfecto". Un tamaño cómodo, pensado para llevar todo lo que necesitas. Se lleva cruzado o al hombro, según la correa elegida, para acompañarte en tu día a día.',
    'Corps de sac noir format "Intégral". Un corps de sac grand format, pensé pour tout ce dont vous avez besoin, sans vous priver de rien. Il se porte à l\'épaule grâce à ses deux hanses incluses.':
      'Cuerpo de bolso negro en formato "El completo". Un cuerpo de bolso grande, pensado para todo lo que necesitas, sin renunciar a nada. Se lleva al hombro con sus dos asas incluidas.',
    'Zipper intérieur et fermeture éclair en forme de coeur.': 'Cremallera interior y tirador en forma de corazón.',
    'Zipper intérieur et fermeture éclair dorée en forme de coeur.': 'Cremallera interior y tirador dorado en forma de corazón.',
    '2 poches intérieures dont une à zip.': '2 bolsillos interiores, uno con cremallera.',
    '1 grande poche intérieure + 2 petites.': '1 bolsillo interior grande + 2 pequeños.',
    '1 grande poche intérieure avec fermeture à pression + 2 petites.': '1 bolsillo interior grande con cierre de presión + 2 pequeños.',
    'Doublure intérieure en simili vegan rouge.': 'Forro interior rojo de piel sintética vegana.',
    'Doublure microfibre intérieure rouge.': 'Forro interior rojo de microfibra.',
    'Un séparateur central vous offrant 2 compartiments de rangement.': 'Un separador central con 2 compartimentos.',
    'Grande surface de rangement, idéal pour transporter un ordinateur portable.': 'Mucho espacio, ideal para llevar un portátil.',
    "Un fermoir aimanté à l'avant pour la fermeture du sac.": 'Un cierre imantado delante para cerrar el bolso.',
    "Fermeture aimantée à l'avant pour la fermeture du sac.": 'Cierre imantado delante para cerrar el bolso.',
    "3 boutons à pression à l'arrière du sac pour clipper le rabat.": '3 botones de presión detrás para fijar la solapa.',
    "3 boutons à pression forte tenue à l'arrière du sac pour clipper le rabat.": '3 botones de presión extrafuertes detrás para fijar la solapa.',
    '4 pieds de protection en métal sous le sac': '4 pies protectores de metal bajo el bolso',
    '2 hanses noires épaule avec anneaux métal amovibles vendues avec le sac.': '2 asas negras de hombro con anillas metálicas extraíbles, incluidas con el bolso.',
    'À personnaliser avec les accessoires de votre choix pour créer le sac idéal. Logo "And Joy" embossé en doré à l\'avant du sac.':
      'Personalízalo con los accesorios que quieras para crear tu bolso ideal. Logo "And Joy" grabado en dorado en la parte delantera.',
    'Matière premium synthétique recyclée labellisée Global Recycled Standards.': 'Material sintético reciclado premium con certificación Global Recycled Standard.',
    'Dessiné en France à Lyon et façonné à la main en Italie, dans un petit atelier Florentin.': 'Diseñado en Lyon (Francia) y hecho a mano en Italia, en un pequeño taller de Florencia.',
  },
  it: {
    'Fermoirs non perforé': 'Chiusure', 'Fermoirs perforés': 'Chiusure forate', 'Choisis ton confort': 'Scegli come portarla',
    'Un charm ?': 'Un charm?', 'Sans charm': 'Senza charm', 'Avec un charm +3€': 'Con un charm +3 €',
    'Sangle épaule avec anneaux dorés': 'Tracolla da spalla con anelli dorati',
    'Corps de sac noir format "essentiel". Un corps de sac au format compact, pensé pour emmener uniquement vos essentiels avec vous. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Corpo borsa nero in formato "L\'essenziale". Un corpo borsa compatto, pensato per portare con te solo l\'essenziale. Si porta a tracolla o a spalla, secondo la tracolla scelta, per accompagnarti in ogni momento della giornata.',
    'Corps de sac noir format "équilibre parfait". Un corps de sac au format confort, pensé pour emmener tout ce dont vous avez besoin. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Corpo borsa nero in formato "Equilibrio perfetto". Un formato comodo, pensato per portare tutto ciò che ti serve. Si porta a tracolla o a spalla, secondo la tracolla scelta, per accompagnarti in ogni momento della giornata.',
    'Corps de sac noir format "Intégral". Un corps de sac grand format, pensé pour tout ce dont vous avez besoin, sans vous priver de rien. Il se porte à l\'épaule grâce à ses deux hanses incluses.':
      'Corpo borsa nero in formato "L\'integrale". Un corpo borsa grande, pensato per tutto ciò che ti serve, senza rinunciare a nulla. Si porta a spalla grazie ai due manici inclusi.',
    'Zipper intérieur et fermeture éclair en forme de coeur.': 'Zip interna e cursore della cerniera a forma di cuore.',
    'Zipper intérieur et fermeture éclair dorée en forme de coeur.': 'Zip interna e cursore dorato della cerniera a forma di cuore.',
    '2 poches intérieures dont une à zip.': '2 tasche interne, di cui una con zip.',
    '1 grande poche intérieure + 2 petites.': '1 tasca interna grande + 2 piccole.',
    '1 grande poche intérieure avec fermeture à pression + 2 petites.': '1 tasca interna grande con bottone a pressione + 2 piccole.',
    'Doublure intérieure en simili vegan rouge.': 'Fodera interna rossa in similpelle vegana.',
    'Doublure microfibre intérieure rouge.': 'Fodera interna rossa in microfibra.',
    'Un séparateur central vous offrant 2 compartiments de rangement.': 'Un divisorio centrale che crea 2 scomparti.',
    'Grande surface de rangement, idéal pour transporter un ordinateur portable.': 'Tanto spazio, ideale per portare un computer portatile.',
    "Un fermoir aimanté à l'avant pour la fermeture du sac.": 'Una chiusura magnetica sul davanti per chiudere la borsa.',
    "Fermeture aimantée à l'avant pour la fermeture du sac.": 'Chiusura magnetica sul davanti per chiudere la borsa.',
    "3 boutons à pression à l'arrière du sac pour clipper le rabat.": '3 bottoni a pressione sul retro per agganciare la patta.',
    "3 boutons à pression forte tenue à l'arrière du sac pour clipper le rabat.": '3 bottoni a pressione extra resistenti sul retro per agganciare la patta.',
    '4 pieds de protection en métal sous le sac': '4 piedini protettivi in metallo sotto la borsa',
    '2 hanses noires épaule avec anneaux métal amovibles vendues avec le sac.': '2 manici neri da spalla con anelli in metallo removibili, inclusi con la borsa.',
    'À personnaliser avec les accessoires de votre choix pour créer le sac idéal. Logo "And Joy" embossé en doré à l\'avant du sac.':
      'Da personalizzare con gli accessori che preferisci per creare la borsa ideale. Logo "And Joy" impresso in oro sul davanti.',
    'Matière premium synthétique recyclée labellisée Global Recycled Standards.': 'Materiale sintetico riciclato premium certificato Global Recycled Standard.',
    'Dessiné en France à Lyon et façonné à la main en Italie, dans un petit atelier Florentin.': 'Disegnata a Lione, in Francia, e realizzata a mano in Italia, in un piccolo laboratorio fiorentino.',
  },
  // Jamais « Leder » seul : la matière, c'est « veganes Kunstleder »
  de: {
    'Fermoirs non perforé': 'Verschlüsse', 'Fermoirs perforés': 'Gelochte Verschlüsse', 'Choisis ton confort': 'Wähle, wie du sie trägst',
    'Un charm ?': 'Ein Anhänger?', 'Sans charm': 'Ohne Anhänger', 'Avec un charm +3€': 'Mit Anhänger +3 €',
    'Sangle épaule avec anneaux dorés': 'Schulterriemen mit goldenen Ringen',
    'Corps de sac noir format "essentiel". Un corps de sac au format compact, pensé pour emmener uniquement vos essentiels avec vous. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Schwarzer Taschenkorpus im Format "Das Wesentliche". Ein kompakter Taschenkorpus für alles, was du wirklich brauchst. Je nach Riemen trägst du ihn quer über dem Körper oder über der Schulter, ganz wie es dein Tag verlangt.',
    'Corps de sac noir format "équilibre parfait". Un corps de sac au format confort, pensé pour emmener tout ce dont vous avez besoin. Il se porte en bandoulière ou à l\'épaule, selon la sangle choisie, pour s\'adapter à toutes vos envies du quotidien.':
      'Schwarzer Taschenkorpus im Format "Perfekte Balance". Ein bequemes Format für alles, was du brauchst. Je nach Riemen trägst du ihn quer über dem Körper oder über der Schulter, ganz wie es dein Tag verlangt.',
    'Corps de sac noir format "Intégral". Un corps de sac grand format, pensé pour tout ce dont vous avez besoin, sans vous priver de rien. Il se porte à l\'épaule grâce à ses deux hanses incluses.':
      'Schwarzer Taschenkorpus im Format "Alles drin". Ein großer Taschenkorpus für alles, was du brauchst, ohne auf etwas zu verzichten. Du trägst ihn mit den zwei mitgelieferten Henkeln über der Schulter.',
    'Zipper intérieur et fermeture éclair en forme de coeur.': 'Innenreißverschluss mit herzförmigem Zipper.',
    'Zipper intérieur et fermeture éclair dorée en forme de coeur.': 'Innenreißverschluss mit goldenem, herzförmigem Zipper.',
    '2 poches intérieures dont une à zip.': '2 Innentaschen, eine davon mit Reißverschluss.',
    '1 grande poche intérieure + 2 petites.': '1 große Innentasche + 2 kleine.',
    '1 grande poche intérieure avec fermeture à pression + 2 petites.': '1 große Innentasche mit Druckknopf + 2 kleine.',
    'Doublure intérieure en simili vegan rouge.': 'Rotes Innenfutter aus veganem Kunstleder.',
    'Doublure microfibre intérieure rouge.': 'Rotes Innenfutter aus Mikrofaser.',
    'Un séparateur central vous offrant 2 compartiments de rangement.': 'Eine Trennwand in der Mitte für 2 Fächer.',
    'Grande surface de rangement, idéal pour transporter un ordinateur portable.': 'Viel Stauraum, ideal für einen Laptop.',
    "Un fermoir aimanté à l'avant pour la fermeture du sac.": 'Ein Magnetverschluss vorne zum Schließen der Tasche.',
    "Fermeture aimantée à l'avant pour la fermeture du sac.": 'Magnetverschluss vorne zum Schließen der Tasche.',
    "3 boutons à pression à l'arrière du sac pour clipper le rabat.": '3 Druckknöpfe auf der Rückseite zum Befestigen der Klappe.',
    "3 boutons à pression forte tenue à l'arrière du sac pour clipper le rabat.": '3 extra starke Druckknöpfe auf der Rückseite zum Befestigen der Klappe.',
    '4 pieds de protection en métal sous le sac': '4 Schutzfüße aus Metall unter der Tasche',
    '2 hanses noires épaule avec anneaux métal amovibles vendues avec le sac.': '2 schwarze Schulterhenkel mit abnehmbaren Metallringen, im Lieferumfang der Tasche.',
    'À personnaliser avec les accessoires de votre choix pour créer le sac idéal. Logo "And Joy" embossé en doré à l\'avant du sac.':
      'Gestalte sie mit den Accessoires deiner Wahl zu deiner Traumtasche. Goldenes, geprägtes "And Joy"-Logo auf der Vorderseite.',
    'Matière premium synthétique recyclée labellisée Global Recycled Standards.': 'Recyceltes synthetisches Premium-Material, zertifiziert nach Global Recycled Standard.',
    'Dessiné en France à Lyon et façonné à la main en Italie, dans un petit atelier Florentin.': 'Entworfen in Lyon (Frankreich) und von Hand gefertigt in Italien, in einer kleinen Werkstatt in Florenz.',
  },
}

// Préfixe des rabats (« Rabat - Noir » → « Klep - Zwart ») et libellés des dimensions (« Haut.17,5 cm x Larg. 27 cm »)
const FLAP: Record<Exclude<Lang, 'fr'>, string> = { nl: 'Klep', en: 'Flap', es: 'Solapa', it: 'Patta', de: 'Klappe' }
const DIMS: Record<Exclude<Lang, 'fr'>, [string, string, string]> = {
  nl: ['Hoogte', 'Breedte', 'Diepte'], en: ['Height', 'Width', 'Depth'], es: ['Alto', 'Ancho', 'Fondo'], it: ['Altezza', 'Larghezza', 'Profondità'],
  de: ['Höhe', 'Breite', 'Tiefe'],
}

// Apostrophes typographiques et espaces : le SaaS mélange ’ et ', et laisse parfois une espace finale
const norm = (s: string) => s.replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim()

const WIZARD_TERMS = WIZARD as unknown as Record<string, { terms?: Dict }>
const LOOKUP: Partial<Record<Lang, Map<string, string>>> = {}
function lookup(lang: Exclude<Lang, 'fr'>): Map<string, string> {
  if (!LOOKUP[lang]) {
    const m = new Map<string, string>()
    for (const [k, v] of Object.entries({ ...(WIZARD_TERMS[lang]?.terms || {}), ...TERMS[lang] })) m.set(norm(k), v)
    LOOKUP[lang] = m
  }
  return LOOKUP[lang]!
}

let current: Lang = 'fr'

/** Langue de la page : code TrueLang ou <html lang> (nl, en-us, es-ES…) ; toute autre langue → français. */
export function setConfiguratorLang(hint?: string | null) {
  const short = String(hint || (typeof document !== 'undefined' ? document.documentElement.lang : '') || '').toLowerCase().split(/[-_]/)[0]
  current = (['nl', 'en', 'es', 'it', 'de'] as const).includes(short as 'nl') ? (short as Lang) : 'fr'
}

export function configuratorLang(): Lang {
  return current
}

/** Libellé de l'interface, avec {variables}. */
export function ct(key: UiKey, vars?: Record<string, string | number>): string {
  const s = (current !== 'fr' && UI[current][key]) || FR_UI[key]
  return vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m)) : s
}

/** Texte venu du configurateur (nom de produit, de catégorie, question…) ; inconnu → tel quel, en français. */
export function cterm(fr: string | undefined | null): string {
  if (!fr || current === 'fr') return fr || ''
  const m = lookup(current)
  const k = norm(fr)
  const hit = m.get(k)
  if (hit) return hit
  const flap = k.match(/^Rabat\s*-\s*(.+)$/i)
  if (flap && m.has(flap[1])) return `${FLAP[current]} - ${m.get(flap[1])}`
  return fr
}

/** Description d'un corps de sac, ligne par ligne. */
function ctext(fr: string | undefined): string | undefined {
  if (!fr || current === 'fr') return fr
  return fr.split('\n').map(l => (l.trim() ? cterm(l) : l)).join('\n')
}

/** Dimensions « Haut.17,5 cm x Larg. 27 cm x Prof.8 cm » dans la langue de la page. */
function cdims(fr: string | undefined): string | undefined {
  if (!fr || current === 'fr') return fr
  const [h, w, d] = DIMS[current]
  let s = fr.replace(/Haut\.\s*/gi, h + ' ').replace(/Larg\.\s*/gi, w + ' ').replace(/Prof\.\s*/gi, d + ' ')
  if (current === 'en') s = s.replace(/(\d),(\d)/g, '$1.$2')
  return s
}

/**
 * Copie de la config pour l'affichage, textes traduits. Les identifiants ne changent pas : le magasin de sélection,
 * le suivi et l'ajout au panier continuent de travailler sur la config d'origine, en français.
 */
export function translateConfig<T extends {
  baseName?: string; dimensions?: string; info?: string; colorFilterTitle?: string
  categories: { name: string; products: { name: string }[] }[]
  configSteps: { text?: string; optionNo?: { label: string }; optionYes?: { label: string } }[]
}>(config: T): T {
  if (current === 'fr') return config
  return {
    ...config,
    baseName: cterm(config.baseName),
    dimensions: cdims(config.dimensions),
    info: ctext(config.info),
    colorFilterTitle: cterm(config.colorFilterTitle),
    categories: config.categories.map(c => ({ ...c, name: cterm(c.name), products: c.products.map(p => ({ ...p, name: cterm(p.name) })) })),
    configSteps: config.configSteps.map(s => ({
      ...s,
      text: s.text ? cterm(s.text) : s.text,
      optionNo: s.optionNo ? { ...s.optionNo, label: cterm(s.optionNo.label) } : s.optionNo,
      optionYes: s.optionYes ? { ...s.optionYes, label: cterm(s.optionYes.label) } : s.optionYes,
    })),
  }
}

/** Montant en centimes au format de la langue (139,00 € ; € 139,00 en néerlandais ; €139.00 en anglais). */
export function cmoney(cents: number): string {
  if (current === 'fr') return (cents / 100).toFixed(2).replace('.', ',') + ' €' // affichage historique, inchangé
  const locale = { fr: 'fr-FR', nl: 'nl-NL', en: 'en-IE', es: 'es-ES', it: 'it-IT', de: 'de-DE' }[current]
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100)
}
